import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { getAuth, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';

interface PricingModalProps {
  open: boolean;
  onClose: () => void;
}

interface PricingPlan {
  id: string;
  name: string;
  price: number;
  period: string;
  originalPrice?: number;
  savings?: number;
  description: string;
  popular?: boolean;
  bestDeal?: boolean;
  features: string[];
}

const plans: PricingPlan[] = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: 99,
    period: '/month',
    description: 'Perfect for getting started',
    features: [
      'Unlimited zones',
      'Advanced scheduling',
      'Priority support',
      'Real-time monitoring',
      '7-day free trial'
    ]
  },
  {
    id: 'quarterly',
    name: '3 Months',
    price: 249,
    period: '/3 months',
    originalPrice: 297,
    savings: 16,
    description: 'Great value for continued use',
    features: [
      'All Monthly features',
      'Extended support',
      '16% savings',
      'Quarterly billing',
      '7-day free trial'
    ]
  },
  {
    id: 'semiannual',
    name: '6 Months',
    price: 499,
    period: '/6 months',
    originalPrice: 594,
    savings: 16,
    description: 'Best value for long-term use',
    popular: true,
    features: [
      'All Quarterly features',
      'Priority feature requests',
      '16% savings',
      'Semi-annual billing',
      '7-day free trial'
    ]
  },
  {
    id: 'annual',
    name: '1 Year',
    price: 849,
    period: '/year',
    originalPrice: 1188,
    savings: 29,
    description: 'Maximum savings',
    bestDeal: true,
    features: [
      'All Semi-annual features',
      'VIP support',
      '29% savings',
      'Annual billing',
      '7-day free trial'
    ]
  }
];

export default function PricingModal({ open, onClose }: PricingModalProps) {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [error, setError] = useState<string>('');

  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    const checkAuth = async () => {
      setAuthLoading(true);
      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          // Check if user exists in webUsers collection
          const webUserDoc = await getDoc(doc(db, 'webUsers', currentUser.uid));
          if (webUserDoc.exists()) {
            setUser({ id: currentUser.uid, ...webUserDoc.data() });
          } else {
            setUser(null);
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        setUser(null);
      } finally {
        setAuthLoading(false);
      }
    };

    if (open) {
      checkAuth();
    }
  }, [open, auth, db]);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      // After login, check if user exists in webUsers
      const webUserDoc = await getDoc(doc(db, 'webUsers', result.user.uid));
      if (webUserDoc.exists()) {
        setUser({ id: result.user.uid, ...webUserDoc.data() });
      } else {
        setError('Please complete your registration first by accessing the Control Panel.');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (plan: PricingPlan) => {
    if (!user) {
      setError('Please log in first to subscribe.');
      return;
    }

    setLoading(true);
    setSelectedPlan(plan.id);
    setError('');

    try {
      // Check if email is verified
      const currentUser = auth.currentUser;
      if (!currentUser?.emailVerified) {
        setError('📧 Please verify your email address before making a purchase. Check your email inbox for verification link.');
        return;
      }

      // Check if user already has active STRIPE subscription (not just trial/free)
      if (user.subscription?.status === 'active' && user.subscription?.plan === 'pro' && user.stripeCustomerId) {
        setError('You already have an active Stripe subscription. Please manage it through your dashboard.');
        return;
      }

      // Call Firebase Function to create checkout session
      const response = await fetch(
        `https://creatempcheckoutsession-riq6fkesra-uc.a.run.app`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            plan: plan.id,
            webUserId: user.id
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();

      // Redirect to Stripe Checkout
      window.location.href = url;

    } catch (error: any) {
      console.error('Subscription error:', error);
      setError(error.message || 'Failed to start subscription process. Please try again.');
    } finally {
      setLoading(false);
      setSelectedPlan('');
    }
  };

  const PlanCard = ({ plan }: { plan: PricingPlan }) => (
    <Card
      sx={{
        position: 'relative',
        height: '100%',
        border: plan.popular ? '2px solid #2e86ab' : '1px solid rgba(0,0,0,0.1)',
        borderRadius: '16px',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(46, 134, 171, 0.15)'
        }
      }}
    >
      {plan.popular && (
        <Chip
          label="Most Popular"
          icon={<StarIcon />}
          sx={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, #2e86ab, #4a90c2)',
            color: 'white',
            fontWeight: 600
          }}
        />
      )}

      {plan.bestDeal && (
        <Chip
          label="Best Deal"
          icon={<TrendingUpIcon />}
          sx={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, #ff6b6b, #ee5a52)',
            color: 'white',
            fontWeight: 600
          }}
        />
      )}

      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box textAlign="center" mb={2}>
          <Typography variant="h6" fontWeight={600} mb={1}>
            {plan.name}
          </Typography>

          <Box display="flex" alignItems="baseline" justifyContent="center" mb={1}>
            <Typography variant="h4" fontWeight={700} color="#2e86ab">
              ${plan.price}
            </Typography>
            <Typography variant="body2" color="text.secondary" ml={0.5}>
              {plan.period}
            </Typography>
          </Box>

          {plan.savings && (
            <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
              <Typography
                variant="body2"
                sx={{ textDecoration: 'line-through', color: 'text.disabled' }}
              >
                ${plan.originalPrice}
              </Typography>
              <Chip
                label={`Save ${plan.savings}%`}
                size="small"
                color="success"
                variant="outlined"
              />
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" mb={2}>
            {plan.description}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box flexGrow={1} mb={3}>
          {plan.features.map((feature, index) => (
            <Box key={index} display="flex" alignItems="center" mb={1}>
              <CheckIcon sx={{ color: '#2e86ab', fontSize: 20, mr: 1 }} />
              <Typography variant="body2">{feature}</Typography>
            </Box>
          ))}
        </Box>

        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={() => handleSubscribe(plan)}
          disabled={loading}
          sx={{
            background: plan.popular || plan.bestDeal ?
              'linear-gradient(135deg, #2e86ab, #4a90c2)' :
              'linear-gradient(135deg, #64748b, #475569)',
            color: 'white',
            fontWeight: 600,
            py: 1.5,
            borderRadius: '12px',
            '&:hover': {
              background: plan.popular || plan.bestDeal ?
                'linear-gradient(135deg, #266b8a, #3d7ba3)' :
                'linear-gradient(135deg, #475569, #334155)',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(46, 134, 171, 0.3)'
            }
          }}
        >
          {loading && selectedPlan === plan.id ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Start Free Trial'
          )}
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          maxHeight: '90vh'
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          backdropFilter: 'blur(8px)'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h5" fontWeight={700}>
          Choose Your Plan
        </Typography>
        <IconButton onClick={onClose} size="large">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {authLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : !user ? (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" mb={2}>
              🔐 Login Required
            </Typography>
            <Typography variant="body1" mb={3} color="text.secondary">
              Please log in to subscribe to Multizone Player Pro
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleGoogleLogin}
              disabled={loading}
              sx={{
                background: 'linear-gradient(135deg, #2e86ab, #4a90c2)',
                fontWeight: 600,
                px: 4,
                py: 1.5
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Login with Google'}
            </Button>
          </Box>
        ) : (
          <>
            <Box textAlign="center" mb={4}>
              <Typography variant="h6" mb={1}>
                Welcome back, {user.profile?.firstName || user.hotelAdmin?.username}!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                All plans include a 7-day free trial. Cancel anytime.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {plans.map((plan) => (
                <Grid item xs={12} sm={6} md={3} key={plan.id}>
                  <PlanCard plan={plan} />
                </Grid>
              ))}
            </Grid>

            <Box textAlign="center" mt={4} pt={3} borderTop="1px solid rgba(0,0,0,0.1)">
              <Typography variant="body2" color="text.secondary">
                🔒 Secure payment processing by Stripe • Cancel anytime • No hidden fees
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}